/**
 * SignatureCanvas.jsx · v2.7.65+E78
 * ══════════════════════════════════
 * Zero-dependency signature pad using the native HTML5 Canvas 2D API.
 * Supports both pointer + touch input (single unified pointerdown/move/up
 * listener set — no per-device fallback). Exposes:
 *
 *   ref.current.toDataURL()   → PNG data URL of the current signature
 *   ref.current.clear()       → wipe the canvas
 *   ref.current.isSigned()    → true iff pixel variance ≥ threshold
 *
 * The `isSigned()` check counts non-background pixel mutations in the
 * ImageData buffer and requires the count to exceed a mathematical
 * variance threshold (`minPixelDelta`, default 200) so that stray
 * accidental strokes still fail validation.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser } from "lucide-react";

const SignatureCanvas = forwardRef(function SignatureCanvas(
  { width = 700, height = 180, minPixelDelta = 200, "data-testid": testId = "signature-canvas" },
  ref,
) {
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const [signed, setSigned] = useState(false);

  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
    const rect = c.getBoundingClientRect();
    c.width  = Math.floor(rect.width  * dpr);
    c.height = Math.floor(rect.height * dpr);
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap    = "round";
    ctx.lineJoin   = "round";
    ctx.lineWidth  = 2.2;
    ctx.strokeStyle = "#111";
    // Paint a subtle background so pixel variance can detect any stroke.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  useEffect(() => {
    resize();
    const onR = () => resize();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, [resize]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    setSigned(true);
  };

  const isSigned = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return false;
    const ctx = c.getContext("2d");
    const img = ctx.getImageData(0, 0, c.width, c.height);
    // Count pixels that deviate from pure white (255,255,255).
    let delta = 0;
    const data = img.data;
    for (let i = 0; i < data.length; i += 4) {
      // If any RGB channel drops below 250, it's a stroke pixel.
      if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
        delta++;
        if (delta > minPixelDelta) return true;
      }
    }
    return false;
  }, [minPixelDelta]);

  useImperativeHandle(ref, () => ({
    clear() {
      const c = canvasRef.current;
      const ctx = c.getContext("2d");
      const rect = c.getBoundingClientRect();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, c.width, c.height);
      resize();
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
      setSigned(false);
    },
    toDataURL() {
      return canvasRef.current?.toDataURL("image/png") || "";
    },
    isSigned,
  }), [resize, isSigned]);

  return (
    <div className="space-y-2">
      <div className="relative bg-white border border-zinc-700 rounded overflow-hidden" style={{ height }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full block touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          onTouchCancel={end}
          data-testid={testId}
        />
        {!signed && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-zinc-400 text-sm italic">
            Sign here
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-zinc-500">
        <span>Draw with your finger, stylus, or mouse.</span>
        <button
          type="button"
          onClick={() => ref?.current?.clear()}
          className="inline-flex items-center gap-1 text-zinc-400 hover:text-amber-300"
          data-testid={`${testId}-clear`}
        >
          <Eraser className="h-3 w-3"/> Clear
        </button>
      </div>
    </div>
  );
});

export default SignatureCanvas;
